import React, { createContext, useState, useContext, useEffect } from 'react';
import apiAxiosInstance from '../utils/axiosInstance';
import { useTeacher } from './TeacherContext';

export interface Learner {
  _id: string;
  firstName: string;
  lastName: string;
  gradeLevel: string;
  dateOfBirth?: string;
  subjectLevels?: {
    math?: number;
    reading?: number;
  };
  ownerId?: string;
  ownerType?: string;
  deletedAt?: Date | null;
}

export interface DeletedLearner {
  _id: string;
  firstName: string;
  lastName: string;
  deletedAt: string;
}

export interface Family {
  _id: string;
  ownerUserId: string;
  name: string;
  learnerIds: string[];
  // Phase 7: soft-deleted learners within their 30-day grace period, split
  // out of learnerIds by GET /api/family/me - see restoreLearner below.
  deletedLearners?: DeletedLearner[];
  subscription?: {
    status?: string;
    planId?: string;
    subscriptionId?: string;
    currentPeriodEnd?: Date;
    billingCycle?: 'monthly' | 'annual';
  };
}

interface FamilyContextType {
  family: Family | null;
  learners: Learner[];
  selectedLearnerId: string | null;
  loading: boolean;
  error: string | null;

  setSelectedLearnerId: (id: string | null) => void;
  createLearner: (learner: {
    firstName: string;
    lastName: string;
    gradeLevel: string;
    dateOfBirth?: string;
    subjectLevels?: { math?: number; reading?: number };
  }) => Promise<Learner>;
  updateLearner: (learnerId: string, updates: Partial<Learner>) => Promise<Learner>;
  deleteLearner: (learnerId: string) => Promise<void>;
  restoreLearner: (learnerId: string) => Promise<void>;
  fetchFamily: () => Promise<void>;
  fetchLearners: () => Promise<void>;
}

const FamilyContext = createContext<FamilyContextType | undefined>(undefined);

// apiAxiosInstance's shared response interceptor rejects with a plain string
// for most non-2xx statuses (only 400/401/timeout/network pass through the
// original Axios error object) - see utils/axiosInstance.ts's response
// interceptor. Extract a display message defensively regardless of which
// shape actually comes back.
function extractErrorMessage(err: unknown, fallback: string): string {
  if (typeof err === 'string') return err;
  if (err && typeof err === 'object') {
    const anyErr = err as any;
    return anyErr.response?.data?.error || anyErr.response?.data?.message || anyErr.message || fallback;
  }
  return fallback;
}

export const FamilyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // FamilyProvider must be nested inside TeacherProvider (see index.tsx) so it
  // can gate family fetches on role. Without this, every teacher page load
  // would fire a guaranteed-403 request to /api/family/me.
  const { teacher, loading: teacherLoading } = useTeacher();
  const [family, setFamily] = useState<Family | null>(null);
  const [learners, setLearners] = useState<Learner[]>([]);
  const [selectedLearnerId, setSelectedLearnerId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);

  const fetchFamily = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await apiAxiosInstance.get('/api/family/me');
      // GET /api/family/me returns { family }
      setFamily(response.data.family);
    } catch (err) {
      // This is a background auto-load (see the init effect below), not a
      // user-initiated action, so a failure here (no family yet, route not
      // mounted because FEATURE_FAMILY_AUTH is off, transient 500, etc.)
      // just means "nothing to show" rather than a hard error to surface.
      // apiAxiosInstance's shared interceptor also collapses most non-2xx
      // responses into a plain string (see extractErrorMessage above), so a
      // reliable status-code branch (e.g. "only treat 404 as expected")
      // isn't available here anyway.
      console.warn('Family fetch unsuccessful (treated as no family yet):', extractErrorMessage(err, 'unknown error'));
      setFamily(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchLearners = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      if (!token || !family || family.learnerIds.length === 0) {
        setLearners([]);
        return;
      }

      // No bulk "list my learners" endpoint exists yet - fetch each by id
      // via the shared students route (GET /api/students/:id returns the
      // student document directly, not wrapped).
      const learnerResponses = await Promise.all(
        family.learnerIds.map(learnerId =>
          apiAxiosInstance.get(`/api/students/${learnerId}`).then(r => r.data)
        )
      );
      setLearners(learnerResponses);
    } catch (err) {
      console.error('Error fetching learners:', err);
      setError(extractErrorMessage(err, 'Failed to fetch learners'));
    } finally {
      setLoading(false);
    }
  };

  // Auto-load family data once TeacherContext confirms this is a parent
  // account. Gated on role so non-parent accounts never call
  // /api/family/me (that route is requireRole('parent') and would 403 for
  // every teacher on every page load otherwise). Without this effect at
  // all, `family` would stay null until some component happened to call
  // fetchFamily() explicitly - OnboardingWizard never did.
  useEffect(() => {
    if (teacherLoading || initialized) return;
    if (teacher?.role === 'parent') {
      setInitialized(true);
      fetchFamily();
    } else if (teacher) {
      // Loaded, but not a parent - nothing to fetch.
      setInitialized(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teacher, teacherLoading, initialized]);

  useEffect(() => {
    if (family) {
      fetchLearners();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [family?._id]);

  const createLearner = async (learner: {
    firstName: string;
    lastName: string;
    gradeLevel: string;
    dateOfBirth?: string;
    subjectLevels?: { math?: number; reading?: number };
  }) => {
    try {
      setError(null);
      const response = await apiAxiosInstance.post('/api/family/learners', learner);
      // POST /api/family/learners returns { learner }
      const newLearner: Learner = response.data.learner;
      setLearners(prev => [...prev, newLearner]);

      // Keep family.learnerIds in sync locally rather than refetching.
      setFamily(prev =>
        prev ? { ...prev, learnerIds: [...prev.learnerIds, newLearner._id] } : prev
      );

      return newLearner;
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to create learner'));
      throw err;
    }
  };

  const updateLearner = async (learnerId: string, updates: Partial<Learner>) => {
    try {
      setError(null);
      const response = await apiAxiosInstance.patch(`/api/family/learners/${learnerId}`, updates);
      // PATCH /api/family/learners/:learnerId returns { learner }
      const updatedLearner: Learner = response.data.learner;
      setLearners(prev => prev.map(l => (l._id === learnerId ? updatedLearner : l)));
      return updatedLearner;
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to update learner'));
      throw err;
    }
  };

  const deleteLearner = async (learnerId: string) => {
    try {
      setError(null);
      await apiAxiosInstance.delete(`/api/family/learners/${learnerId}`);

      const removed = learners.find(l => l._id === learnerId);
      setLearners(prev => prev.filter(l => l._id !== learnerId));
      setFamily(prev => {
        if (!prev) return prev;
        const deletedEntry: DeletedLearner = {
          _id: learnerId,
          firstName: removed?.firstName || '',
          lastName: removed?.lastName || '',
          deletedAt: new Date().toISOString(),
        };
        return {
          ...prev,
          learnerIds: prev.learnerIds.filter(id => id !== learnerId),
          deletedLearners: [...(prev.deletedLearners || []), deletedEntry],
        };
      });
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to delete learner'));
      throw err;
    }
  };

  // Phase 7: undo a soft delete within the 30-day grace period. Mirrors
  // deleteLearner's local-state-sync style rather than refetching - the
  // fetchLearners effect only re-runs on family._id change (see above), so
  // a restore must patch `learners`/`family` directly.
  const restoreLearner = async (learnerId: string) => {
    try {
      setError(null);
      const response = await apiAxiosInstance.patch(`/api/family/learners/${learnerId}/restore`);
      const restoredLearner: Learner = response.data.learner;

      setLearners(prev => [...prev, restoredLearner]);
      setFamily(prev =>
        prev
          ? {
              ...prev,
              learnerIds: [...prev.learnerIds, learnerId],
              deletedLearners: (prev.deletedLearners || []).filter(l => l._id !== learnerId),
            }
          : prev
      );
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to restore learner'));
      throw err;
    }
  };

  return (
    <FamilyContext.Provider
      value={{
        family,
        learners,
        selectedLearnerId,
        loading,
        error,
        setSelectedLearnerId,
        createLearner,
        updateLearner,
        deleteLearner,
        restoreLearner,
        fetchFamily,
        fetchLearners
      }}
    >
      {children}
    </FamilyContext.Provider>
  );
};

export const useFamily = (): FamilyContextType => {
  const context = useContext(FamilyContext);
  if (context === undefined) {
    throw new Error('useFamily must be used within FamilyProvider');
  }
  return context;
};
